import { Component } from "../lib/component.ts";

const IDLE_TIMEOUT = 15_000;
const MOBILE_QUERY = "(max-aspect-ratio: 1/1)";

const INITIAL_PLAY = 0;
const IDLE_WAITING = 1;
const IDLE_ACTIVE = 2;

interface VideoSource {
  url: string;
  mime_type: string;
}

function isVideoSource(value: unknown): value is VideoSource {
  if (typeof value !== "object" || value === null) return false;
  if (!("url" in value) || !("mime_type" in value)) return false;
  return typeof value.url === "string" && typeof value.mime_type === "string";
}

function parseVideoSources(json: string): VideoSource[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const sources: VideoSource[] = [];
  for (const item of parsed) {
    if (isVideoSource(item)) sources.push(item);
  }
  return sources.length > 0 ? sources : null;
}

class WorldIntro extends Component {
  #video: HTMLVideoElement | null = null;

  #state: number = INITIAL_PLAY;

  #idleTimer = 0;

  #pauseController: AbortController | null = null;

  #menuOpen = false;

  #hasPlayed = false;

  #visibilityObserver: IntersectionObserver | null = null;

  #visibleTargets: Set<Element> = new Set();

  #mobileQuery = window.matchMedia(MOBILE_QUERY);

  connectedCallback(): void {
    super.connectedCallback();

    if (!this.dataset.shadowVideo && !this.dataset.shadowVideoMobile) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.Shopify?.designMode) return;

    this.#mobileQuery.addEventListener("change", this.#handleQueryChange, {
      signal: this.signal,
    });

    document.addEventListener("scroll", this.#handleActivity, {
      signal: this.signal,
      capture: true,
      passive: true,
    });
    document.addEventListener("pointerdown", this.#handleActivity, {
      signal: this.signal,
      capture: true,
    });
    document.addEventListener("keydown", this.#handleActivity, {
      signal: this.signal,
      capture: true,
    });
    document.addEventListener("visibilitychange", this.#handleVisibility, {
      signal: this.signal,
    });

    const panel = document.getElementById("header-menu");
    if (panel) {
      panel.addEventListener("open", this.#handleMenuOpen, { signal: this.signal });
      panel.addEventListener("close", this.#handleMenuClose, { signal: this.signal });
    }

    this.#visibilityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#visibleTargets.add(entry.target);
        else this.#visibleTargets.delete(entry.target);
      }
      if (this.#visibleTargets.size > 0) this.#activate();
      else this.#deactivate();
    });

    this.#visibilityObserver.observe(this);
    const introBlock = this.closest(".shopify-block");
    const companion = introBlock?.nextElementSibling?.querySelector(":scope > world-gallery");
    if (companion) this.#visibilityObserver.observe(companion);
  }

  disconnectedCallback(): void {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#visibleTargets.clear();
    this.#deactivate({ immediate: true });
    super.disconnectedCallback();
  }

  #activate(): void {
    if (this.#video) {
      if (this.#state === INITIAL_PLAY || this.#state === IDLE_ACTIVE) {
        this.#showVideo();
      }
      return;
    }

    const video = this.#createVideo();
    if (!video) return;
    this.#video = video;

    if (this.#hasPlayed) {
      this.#state = IDLE_WAITING;
      this.#startIdleTimer();
    } else {
      this.#hasPlayed = true;
      this.#state = INITIAL_PLAY;
      this.#video.loop = false;
      this.#video.addEventListener("ended", this.#handleInitialEnd, { signal: this.signal });
      this.#showVideo();
    }
  }

  #deactivate({ immediate = false }: { immediate?: boolean } = {}): void {
    window.clearTimeout(this.#idleTimer);
    if (!this.#video) return;
    const video = this.#video;
    const wasActive = video.classList.contains("video-overlay__video--active");
    video.classList.remove("video-overlay__video--active");
    this.#pauseController?.abort();
    this.#pauseController = null;

    if (immediate || !wasActive) {
      video.pause();
      video.remove();
      this.#video = null;
      return;
    }

    const controller = new AbortController();
    this.#pauseController = controller;
    video.addEventListener(
      "transitionend",
      () => {
        video.pause();
        video.remove();
        if (this.#video === video) this.#video = null;
      },
      { once: true, signal: controller.signal },
    );
  }

  #createVideo(): HTMLVideoElement | null {
    const sources = this.#resolveSources();
    if (!sources || sources.length === 0) return null;

    const video = document.createElement("video");
    video.className = "video-overlay__video";
    video.muted = true;
    video.playsInline = true;

    for (const source of sources) {
      const el = document.createElement("source");
      el.src = source.url;
      el.type = source.mime_type;
      video.appendChild(el);
    }

    document.getElementById("video-overlay")?.appendChild(video);
    return video;
  }

  #resolveSources(): VideoSource[] | null {
    const desktop = this.dataset.shadowVideo;
    const mobile = this.dataset.shadowVideoMobile;
    const json = this.#mobileQuery.matches ? (mobile ?? desktop) : (desktop ?? mobile);
    return json ? parseVideoSources(json) : null;
  }

  #transition(newState: number): void {
    if (newState === this.#state) return;
    this.#state = newState;

    window.clearTimeout(this.#idleTimer);

    switch (newState) {
      case IDLE_WAITING:
        this.#hideVideo();
        this.#startIdleTimer();
        break;
      case IDLE_ACTIVE:
        if (this.#video) this.#video.loop = true;
        this.#showVideo();
        break;
    }
  }

  #showVideo(): void {
    if (!this.#video || this.#menuOpen) return;
    this.#pauseController?.abort();
    this.#video.classList.add("video-overlay__video--active");
    this.#video.play().catch(() => {});
  }

  #hideVideo(): void {
    if (!this.#video || !this.#video.classList.contains("video-overlay__video--active")) return;
    this.#video.classList.remove("video-overlay__video--active");

    this.#pauseController?.abort();
    const controller = new AbortController();
    this.#pauseController = controller;
    this.#video.addEventListener("transitionend", () => this.#video?.pause(), {
      once: true,
      signal: controller.signal,
    });
  }

  #startIdleTimer(): void {
    window.clearTimeout(this.#idleTimer);
    this.#idleTimer = window.setTimeout(() => {
      this.#transition(IDLE_ACTIVE);
    }, IDLE_TIMEOUT);
  }

  #handleInitialEnd = (): void => {
    this.#transition(IDLE_WAITING);
  };

  #handleActivity = (): void => {
    if (!this.#video || this.#state === INITIAL_PLAY) return;
    if (this.#state === IDLE_ACTIVE) {
      this.#transition(IDLE_WAITING);
    } else if (this.#state === IDLE_WAITING) {
      window.clearTimeout(this.#idleTimer);
      this.#startIdleTimer();
    }
  };

  #handleVisibility = (): void => {
    if (!this.#video) return;
    if (document.hidden) {
      this.#video?.pause();
      window.clearTimeout(this.#idleTimer);
    } else if (this.#state === INITIAL_PLAY || this.#state === IDLE_ACTIVE) {
      this.#video?.play().catch(() => {});
    } else if (this.#state === IDLE_WAITING) {
      this.#startIdleTimer();
    }
  };

  #handleQueryChange = (): void => {
    if (!this.#video) return;
    if (!this.dataset.shadowVideo || !this.dataset.shadowVideoMobile) return;

    const prevState = this.#state;
    this.#deactivate({ immediate: true });
    if (prevState === INITIAL_PLAY) this.#hasPlayed = false;
    if (this.#visibleTargets.size === 0) return;
    this.#activate();
    if (prevState === IDLE_ACTIVE) this.#transition(IDLE_ACTIVE);
  };

  #handleMenuOpen = (): void => {
    this.#menuOpen = true;
    this.#hideVideo();
  };

  #handleMenuClose = (): void => {
    this.#menuOpen = false;
    if (this.#state === INITIAL_PLAY || this.#state === IDLE_ACTIVE) {
      this.#showVideo();
    }
  };
}

if (!customElements.get("world-intro")) {
  customElements.define("world-intro", WorldIntro);
}
